import { useMemo, useState } from 'react';
import { Loader2, RotateCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useInvestigation, useInvestigations, useStartInvestigation } from '@/queries/investigations';
import { isStale } from '@/types/investigation';
import { ResultBody } from './ResultBody';
import { DebugTab } from './DebugTab';
import { ProposedActions } from './ProposedActions';
import { triageDepthLabel } from './triageLabels';
import type { InvestigationStep, ProposalStatus } from '@/types/investigation';
import type { InvestigationWithTriage } from '@/queries/investigations';

// Anything else still needs a person: proposed, stale or failed (dismissable), or a stuck applying.
const DECIDED: ProposalStatus[] = ['applied', 'dismissed'];

function byCreatedAtDesc(a: InvestigationWithTriage, b: InvestigationWithTriage): number {
  return Date.parse(b.createdAt) - Date.parse(a.createdAt);
}

export function InvestigationPanel({ ticketId, onUseAsReply }: { ticketId: string; onUseAsReply: (text: string) => void }) {
  const { data } = useInvestigations(ticketId);
  const start = useStartInvestigation(ticketId);
  const [historyOpen, setHistoryOpen] = useState(false);

  const runs = useMemo(() => [...(data?.data ?? [])].sort(byCreatedAtDesc), [data]);
  const latest = runs[0] as InvestigationWithTriage | undefined;
  const previousRuns = runs.slice(1);

  const isLive = Boolean(latest && (latest.status === 'queued' || latest.status === 'running') && !isStale(latest));
  // List responses project debug out, so the live step feed reads it off the
  // detail endpoint for the run currently in progress.
  const { data: detail } = useInvestigation(ticketId, latest?._id ?? '', isLive);

  if (!latest) return null;

  const rerun = () => start.mutate();

  return (
    <div className="px-4 py-3 space-y-2" data-testid="investigation-panel">
      {latest.status === 'completed' && (
        <Tabs defaultValue="result">
          <TabsList variant="line">
            <TabsTrigger value="result">Result</TabsTrigger>
            <TabsTrigger value="debug">Debug</TabsTrigger>
          </TabsList>
          <TabsContent value="result">
            <ResultBody
              investigation={latest}
              onUseAsReply={onUseAsReply}
              onRunFullInvestigation={() => start.mutate({ forceDepth: 'deep' })}
              pending={start.isPending}
            />
          </TabsContent>
          <TabsContent value="debug">
            <DebugTab ticketId={ticketId} invId={latest._id} />
          </TabsContent>
        </Tabs>
      )}

      {isLive && <LiveCard run={latest} steps={detail?.debug?.steps ?? []} />}

      {(latest.status === 'running' || latest.status === 'queued') && isStale(latest) && (
        <StoppedCard
          tone="neutral"
          title={latest.status === 'queued' ? 'Investigation never started' : 'Investigation stalled'}
          body={latest.status === 'queued' ? 'No worker picked the run up within 3 minutes.' : 'No progress for over 3 minutes.'}
          onRerun={rerun}
          pending={start.isPending}
          ticketId={ticketId}
          invId={latest._id}
        />
      )}

      {latest.status === 'failed' &&
        (latest.error === 'Investigation cancelled' ? (
          <StoppedCard
            tone="neutral"
            title="Cancelled"
            body={null}
            onRerun={rerun}
            pending={start.isPending}
            ticketId={ticketId}
            invId={latest._id}
          />
        ) : (
          <StoppedCard
            tone="destructive"
            title="Investigation failed"
            body={latest.error ?? 'Unknown error'}
            onRerun={rerun}
            pending={start.isPending}
            ticketId={ticketId}
            invId={latest._id}
          />
        ))}

      {!isLive && (latest.proposedActions?.length ?? 0) > 0 && (
        <ProposedActions ticketId={ticketId} invId={latest._id} proposals={latest.proposedActions ?? []} />
      )}

      {/* A re-run must not strand an earlier run's undecided proposals out of reach */}
      {previousRuns.map(r => {
        const open = (r.proposedActions ?? []).filter(p => !DECIDED.includes(p.status));
        return open.length > 0 ? (
          <ProposedActions
            key={r._id}
            ticketId={ticketId}
            invId={r._id}
            proposals={open}
            title={`Proposed changes from the run on ${new Date(r.createdAt).toLocaleString()}`}
          />
        ) : null;
      })}

      {previousRuns.length > 0 && (
        <div>
          <button type="button" className="text-xs text-muted-foreground hover:underline" onClick={() => setHistoryOpen(o => !o)}>
            Previous runs ({previousRuns.length})
          </button>
          {historyOpen && (
            <ul className="mt-1 space-y-1">
              {previousRuns.map(r => (
                <li key={r._id} className="text-xs text-muted-foreground">
                  {r.status} — {new Date(r.createdAt).toLocaleString()}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function LiveCard({ run, steps }: { run: InvestigationWithTriage; steps: InvestigationStep[] | undefined }) {
  const tail = (steps ?? []).slice(-6);
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 pb-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm font-medium">
          {run.triage
            ? run.maxSteps != null
              ? `Investigating… step ${steps?.length ?? 0} of ${run.maxSteps}`
              : `Investigating… step ${steps?.length ?? 0}`
            : 'Triaging…'}
        </span>
        {run.triage && (
          <Badge variant="secondary" title={run.triage.reasoning}>
            Triaged: {triageDepthLabel(run.triage.depth)}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-1 pt-0">
        {tail?.map((s, i) => (
          <div key={i} className="text-xs text-muted-foreground">
            {s.tool} — {s.durationMs}ms
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function StoppedCard({
  tone,
  title,
  body,
  onRerun,
  pending,
  ticketId,
  invId
}: {
  tone: 'neutral' | 'destructive';
  title: string;
  body: string | null;
  onRerun: () => void;
  pending: boolean;
  ticketId: string;
  invId: string;
}) {
  const [debugOpen, setDebugOpen] = useState(false);
  return (
    <Card className={tone === 'destructive' ? 'border-destructive' : undefined}>
      <CardHeader className="pb-2">
        <span className={`text-sm font-medium ${tone === 'destructive' ? 'text-destructive' : ''}`}>{title}</span>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {body && <p className="text-xs text-muted-foreground">{body}</p>}
        <div className="flex items-center gap-3">
          <Button size="xs" variant="outline" disabled={pending} onClick={onRerun}>
            {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
            Re-run
          </Button>
          <button type="button" className="text-xs text-muted-foreground hover:underline" onClick={() => setDebugOpen(o => !o)}>
            {debugOpen ? 'Hide debug' : 'Debug'}
          </button>
        </div>
        {debugOpen && <DebugTab ticketId={ticketId} invId={invId} />}
      </CardContent>
    </Card>
  );
}
